import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Calendar, Trophy, Settings, LogOut, User as UserIcon, Medal, Shield, UserCircle, ClipboardList, Crosshair } from 'lucide-react';
import { AdminToasts } from '../common/AdminToasts';
import { useAuth } from '../AuthProvider';
import { cn } from '../../lib/utils';
import { canAccessFeatureFromSnapshot } from '../../lib/feature-access';
import { motion } from 'motion/react';
import { useApiJson } from '../../hooks/useApi';
import type { QuizCampaignSummary } from '../../types';

/** Survives Layout remounts (each page wraps its own Layout). Stale nav is acceptable. */
let cachedQuizNavVisible: boolean | undefined;
let cachedLeaderboardNavVisible: boolean | undefined;

type NavItem = {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  active: (pathname: string, search: string) => boolean;
};

export function Layout({ children }: { children: React.ReactNode }) {
  const { member, isAdmin, isOrganizer, isSuperAdmin, featureAccess, signOut } = useAuth();
  const location = useLocation();
  const organizerNav = isOrganizer && !isAdmin;
  const { data: summary } = useApiJson<QuizCampaignSummary>(organizerNav ? null : '/api/quiz-campaigns/summary');

  if (summary != null) {
    cachedQuizNavVisible = summary.quizNavVisible;
    cachedLeaderboardNavVisible = summary.leaderboardNavVisible;
  }

  const quizNavVisible = cachedQuizNavVisible ?? summary?.quizNavVisible;
  const leaderboardNavVisible = cachedLeaderboardNavVisible ?? summary?.leaderboardNavVisible;

  const navItems = useMemo(() => {
    const exact = (path: string) => (pathname: string) => pathname === path;

    if (organizerNav) {
      const items: NavItem[] = [
        {
          name: 'Events',
          path: '/admin',
          icon: Calendar,
          active: (pathname, search) =>
            pathname === '/admin' && !search.includes('regs=') && !search.includes('view=registrations'),
        },
        {
          name: 'Registrations',
          path: '/admin?view=registrations',
          icon: ClipboardList,
          active: (pathname, search) =>
            pathname === '/admin' && (search.includes('view=registrations') || search.includes('regs=')),
        },
        {
          name: 'Scoring',
          path: '/admin/scoring',
          icon: Crosshair,
          active: (pathname) => pathname === '/admin/scoring' || pathname.startsWith('/admin/scoring/'),
        },
      ];
      return items;
    }

    const items: NavItem[] = [{ name: 'Dashboard', path: '/', icon: Home, active: exact('/') }];

    if (quizNavVisible && canAccessFeatureFromSnapshot(featureAccess, 'quiz')) {
      items.push({ name: 'Quiz', path: '/quiz', icon: Trophy, active: exact('/quiz') });
    }
    if (leaderboardNavVisible && canAccessFeatureFromSnapshot(featureAccess, 'leaderboard')) {
      items.push({ name: 'Leaderboard', path: '/leaderboard', icon: Medal, active: exact('/leaderboard') });
    }

    if (canAccessFeatureFromSnapshot(featureAccess, 'events')) {
      items.push({ name: 'Events', path: '/events', icon: Calendar, active: exact('/events') });
    }

    items.push({ name: 'Profile', path: '/profile', icon: UserCircle, active: exact('/profile') });

    if (isAdmin && canAccessFeatureFromSnapshot(featureAccess, 'members')) {
      items.push({ name: 'Members', path: '/members', icon: Users, active: exact('/members') });
    }
    if (isAdmin && canAccessFeatureFromSnapshot(featureAccess, 'admin')) {
      items.push({ name: 'Admin', path: '/admin', icon: Settings, active: exact('/admin') });
    }
    if (isSuperAdmin) {
      items.push({ name: 'Super Admin', path: '/super-admin', icon: Shield, active: exact('/super-admin') });
    }

    return items;
  }, [isAdmin, isSuperAdmin, organizerNav, featureAccess, quizNavVisible, leaderboardNavVisible]);

  return (
    <div className="min-h-screen bg-[var(--color-surface-0)] text-[var(--color-text-primary)] font-sans">
      <AdminToasts />
      {/* ── Mobile header ── */}
      <header className="md:hidden sticky top-0 z-50 bg-[var(--color-surface-1)] border-b border-[var(--color-border)] px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <img src="/logo-ui.png?v=20261103" alt="SMYM Chennai" className="w-8 h-8 object-contain" />
          <h1 className="font-semibold text-lg tracking-tight">SMYM Chennai</h1>
        </div>
        {member && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] flex items-center justify-center overflow-hidden border border-[var(--color-border)]">
              {member.photoURL ? (
                <img src={member.photoURL} alt={member.name || ''} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-4 h-4 text-[var(--color-text-tertiary)]" />
              )}
            </div>
          </div>
        )}
      </header>

      <div className="flex flex-col md:flex-row min-h-screen">
        {/* ── Desktop sidebar ── */}
        <aside className="hidden md:flex flex-col w-60 bg-[var(--color-surface-1)] border-r border-[var(--color-border)] p-6 sticky top-0 h-screen">
          <div className="mb-10 flex items-center gap-3">
            <img src="/logo-ui.png?v=20261103" alt="SMYM Chennai" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="font-semibold text-lg tracking-tight">SMYM Chennai</h1>
              <p className="text-xs text-[var(--color-text-tertiary)]">
                Shree Maheshwari Yuva Mandal
              </p>
            </div>
          </div>

          <nav className="flex-1 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.active(location.pathname, location.search);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'relative flex items-center gap-3 px-4 py-3 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)]',
                    isActive
                      ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text-primary)]',
                  )}
                >
                  {isActive && (
                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-[var(--color-accent)]" />
                  )}
                  <Icon className={cn('w-5 h-5', isActive ? 'text-[var(--color-accent)]' : '')} />
                  <span className="font-medium text-sm">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {member && (
            <div className="mt-auto pt-4 border-t border-[var(--color-border)] space-y-3">
              <div className="flex items-center gap-3 px-2">
                <div className="w-9 h-9 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] flex items-center justify-center overflow-hidden border border-[var(--color-border)] shrink-0">
                  {member.photoURL ? (
                    <img src={member.photoURL} alt={member.name || ''} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                  ) : (
                    <UserIcon className="w-4 h-4 text-[var(--color-text-tertiary)]" />
                  )}
                </div>
                <div className="flex-1 overflow-hidden">
                  <p className="font-semibold text-sm truncate text-[var(--color-text-primary)]">{member.name}</p>
                  <p className="text-xs text-[var(--color-text-tertiary)] truncate">{member.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => void signOut()}
                className="flex items-center gap-3 w-full px-4 py-2.5 rounded-[var(--radius-md)] text-[var(--color-text-secondary)] hover:bg-[var(--color-error-subtle)] hover:text-[var(--color-error)] transition-colors duration-[var(--duration-fast)]"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-medium text-sm">Logout</span>
              </button>
            </div>
          )}
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {children}
          </motion.div>
        </main>

        {/* ── Mobile bottom tab bar ── */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[var(--color-surface-1)] border-t border-[var(--color-border)] flex justify-around items-stretch px-2 z-50">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.active(location.pathname, location.search);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 py-2 px-3 relative min-h-[52px]',
                  isActive
                    ? 'text-[var(--color-accent)]'
                    : 'text-[var(--color-text-tertiary)]',
                )}
              >
                {isActive && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[2px] rounded-full bg-[var(--color-accent)]" />
                )}
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
